import { useEffect, useState } from "react";
// LocalBusiness schema is provided by ServiceReviewSchema component
import { useRoute } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Breadcrumb from "@/components/Breadcrumb";
import FAQSection from "@/components/FAQSection";
import FAQSchema, { commercialPaintingFAQs, residentialPaintingFAQs } from "@/components/FAQSchema";
import { HowToSchema } from "@/components/HowToSchema";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import GoogleReviews from "@/components/GoogleReviews";
import ServiceSchema from "@/components/ServiceSchema";
import { OpenGraphTags } from "@/components/OpenGraphTags";
import ServiceReviewSchema from "@/components/ServiceReviewSchema";
import { CanonicalTag } from "@/components/CanonicalTag";
import { Phone, Mail, MapPin, Star, Check, ChevronRight } from "lucide-react";
import { Link } from "wouter";
import { getMetaKeywords } from "@/data/metaKeywords";
import { trackCTAClick } from "@/lib/analytics";

interface RelatedArticle {
  slug: string;
  title: string;
  excerpt: string;
}

interface ServiceDetail {
  slug: string;
  title: string;
  shortTitle: string;
  description: string;
  longDescription: string;
  benefits: string[];
  process: string[];
  image: string;
  icon: any;
  metaDescription: string;
  metaKeywords: string;
  relatedArticles?: RelatedArticle[];
}

const serviceDetails: Record<string, ServiceDetail> = {
  "cabinet-painting-santa-barbara": {
    slug: "cabinet-painting-santa-barbara",
    title: "Cabinet Painting & Refinishing Services in Santa Barbara",
    shortTitle: "Cabinet Painting",
    description: "Expert kitchen and bathroom cabinet refinishing and repainting services",
    longDescription: "Replacing kitchen cabinets can cost $10,000–$30,000 or more. Cabinet painting and refinishing by Danis Painting Co. delivers the same dramatic transformation for a fraction of the price. Our licensed painters specialize in factory-smooth finishes on wood, MDF, laminate, and thermofoil cabinets throughout Santa Barbara, Ventura, and Los Angeles Counties. We use professional-grade primers and topcoats — including Benjamin Moore Advance and Sherwin-Williams Emerald Urethane — that cure to a hard, washable finish that resists chipping, yellowing, and daily wear. Every project begins with a thorough degreasing and sanding process to ensure maximum adhesion. Hardware is removed, doors and drawer fronts are sprayed off-site in a controlled environment, and boxes are brush-and-rolled on-site with careful masking. The result is a kitchen that looks brand new without the mess, timeline, or cost of a full renovation. Whether you want crisp white Shaker cabinets, a bold navy island, or a warm greige two-tone look, our color consultants help you choose the perfect palette to complement your countertops, flooring, and lighting.",
    benefits: [
      "Save 60–80% compared to full cabinet replacement",
      "Factory-smooth spray finish on doors and drawer fronts",
      "Hard, washable topcoat resists chipping and yellowing",
      "Works on wood, MDF, laminate, and thermofoil cabinets",
      "Free color consultation with professional paint samples",
      "Completed in 3–5 days with minimal kitchen downtime",
      "Eco-friendly low-VOC paint options available",
      "Increases home resale value and buyer appeal",
    ],
    process: [
      "In-home consultation: we assess cabinet material, condition, and discuss your color goals and finish preferences",
      "Full degreasing and scuff-sanding of all surfaces to ensure maximum paint adhesion",
      "Hardware removal, door and drawer front labeling, and careful masking of countertops and appliances",
      "Application of professional-grade bonding primer — critical for long-term durability on all cabinet materials",
      "Off-site spray finishing of doors and drawer fronts in a dust-free environment for a flawless factory look",
      "On-site brush-and-roll of cabinet boxes with careful cut-in work around hinges and interior corners",
      "Final inspection, hardware reinstallation, and touch-up of any imperfections before project sign-off",
    ],
    image: "/images/cabinet-painting.jpg",
    icon: "Sparkles",
    metaDescription: "Professional cabinet painting and refinishing services in Santa Barbara, Ventura, and Los Angeles. Factory-smooth finish, 60% less than replacement. Free quote!",
    metaKeywords: "cabinet painting Santa Barbara, kitchen cabinet refinishing, bathroom cabinet painting, cabinet refinishing services, cabinet painting contractor, kitchen cabinet painting Los Angeles, cabinet refinishing Ventura",
  },
  "interior-exterior-painting-santa-barbara": {
    slug: "interior-exterior-painting-santa-barbara",
    title: "Interior & Exterior Painting Services in Santa Barbara",
    shortTitle: "Interior & Exterior Painting",
    description: "Professional interior and exterior painting services for walls, ceilings, and trim",
    longDescription: "Your home deserves a paint job that looks exceptional on day one and still looks great five years later. Danis Painting Co. has been delivering premium interior and exterior painting services across Santa Barbara, Ventura, and Los Angeles Counties for over 30 years. Our interior painting services cover everything from single accent walls to whole-house repaints — living rooms, bedrooms, kitchens, bathrooms, ceilings, and stairwells. We use premium Benjamin Moore and Sherwin-Williams paints in the finish best suited to each surface: eggshell for living areas, semi-gloss for kitchens and baths, and flat for ceilings. On the exterior, Southern California's intense UV radiation, coastal salt air, and seasonal moisture demand paints that go beyond standard latex. We use 100% acrylic elastomeric and premium exterior formulas that flex with temperature changes, resist fading, and block moisture intrusion. Every exterior project includes thorough pressure washing, caulking of gaps and cracks, spot priming of bare wood, and careful masking of windows, trim, and hardscape. Whether you own a Spanish Colonial in Montecito, a mid-century modern in Ojai, or a craftsman bungalow in Ventura, our team has the experience and attention to detail to make your property shine.",
    benefits: [
      "30+ years of experience painting homes across Southern California",
      "Free professional color consultation with paint samples",
      "Premium Benjamin Moore and Sherwin-Williams paints only",
      "Thorough surface preparation — the foundation of a lasting finish",
      "Complete furniture protection and daily cleanup on interior projects",
      "Caulking, patching, and minor repairs included on exterior projects",
      "Licensed, bonded, and insured — full peace of mind",
      "Satisfaction guarantee on every project",
    ],
    process: [
      "Free on-site estimate: we measure the project, assess surface conditions, and discuss your color and finish goals",
      "Color consultation: we bring paint chips and sample boards to help you visualize the final result before committing",
      "Interior prep: furniture moved and covered, floors protected with drop cloths, trim and fixtures masked",
      "Exterior prep: pressure washing, caulking of gaps, spot priming of bare wood and repaired areas",
      "Priming: full prime coat applied wherever needed for maximum adhesion and color accuracy",
      "Expert painting: multiple coats applied with professional brushes, rollers, and airless sprayers as appropriate",
      "Final walkthrough: we inspect every surface with you and address any touch-ups before considering the job complete",
    ],
    image: "/images/interior-painting.jpg",
    icon: "Paintbrush",
    metaDescription: "Interior and exterior painting services in Santa Barbara, Ventura, and Los Angeles. 30+ years experience, premium paints, free color consultation. Licensed & insured.",
    metaKeywords: "interior painting Santa Barbara, exterior painting, house painting services, professional painters, painting contractor, interior painting Los Angeles, exterior painting Ventura, residential painting contractor",
  },
  "exterior-painting-santa-barbara": {
    slug: "exterior-painting-santa-barbara",
    title: "Exterior Painting Services in Santa Barbara",
    shortTitle: "Exterior Painting",
    description: "High-quality exterior painting to protect and beautify your property",
    longDescription: "The exterior of your home is its first impression — and in Southern California's climate, it faces relentless UV radiation, coastal salt air, and seasonal rain that can fade, crack, and peel inferior paint jobs within just a few years. Danis Painting Co. specializes in exterior painting that stands up to the California climate while dramatically improving your home's curb appeal and market value. We work on all exterior surfaces: stucco, wood siding, fiber cement (Hardie board), brick, concrete block, and EIFS. Our process begins with a thorough inspection to identify peeling paint, wood rot, failing caulk, and moisture intrusion points — all of which are addressed before a single drop of paint is applied. We use only premium 100% acrylic exterior paints from Benjamin Moore and Sherwin-Williams, including elastomeric formulas for stucco that bridge hairline cracks and create a waterproof membrane. Our crews are experienced with all architectural styles common to the region: Spanish Colonial, Mediterranean, Craftsman, Ranch, and Contemporary. We protect all landscaping, hardscape, windows, and doors with professional masking and drop cloths, and we leave your property cleaner than we found it.",
    benefits: [
      "Premium 100% acrylic and elastomeric exterior paints for maximum durability",
      "UV-resistant formulas that resist fading in intense Southern California sun",
      "Thorough prep: pressure washing, caulking, wood rot repair, and spot priming",
      "Works on stucco, wood siding, Hardie board, brick, and concrete block",
      "Improves curb appeal and increases property resale value",
      "Licensed, bonded, and insured with 30+ years of local experience",
      "Full property protection — landscaping, windows, and hardscape masked",
      "Satisfaction guarantee and warranty on all exterior projects",
    ],
    process: [
      "Free on-site inspection: we assess all exterior surfaces, identify problem areas, and provide a detailed written estimate",
      "Surface preparation: thorough pressure washing to remove dirt, mold, mildew, and chalking paint",
      "Repairs: caulking of gaps around windows and trim, patching of cracks in stucco, and repair of any wood rot",
      "Masking: professional protection of all windows, doors, trim, landscaping, and hardscape",
      "Priming: spot priming of bare wood and repaired areas; full prime coat on new surfaces or major color changes",
      "Painting: multiple coats of premium exterior paint applied with professional brushes, rollers, and airless sprayers",
      "Final inspection: complete walkthrough with the homeowner to ensure every surface meets our quality standard",
    ],
    image: "/images/exterior-painting.jpg",
    icon: "HomeIcon",
    metaDescription: "Exterior painting services in Santa Barbara, Ventura, and Los Angeles. UV-resistant premium paints, thorough prep, 30+ years experience. Licensed & insured. Free quote!",
    metaKeywords: "exterior painting Santa Barbara, house painting, weather-resistant paint, exterior house painters, painting contractor, exterior painting Los Angeles, stucco painting, exterior painting Ventura",
  },
  "roof-coating-repair-santa-barbara": {
    slug: "roof-coating-repair-santa-barbara",
    title: "Roof Coating & Repair Services in Santa Barbara",
    shortTitle: "Roof Coating & Repair",
    description: "Comprehensive roof coating and repair services with GE Enduris protection",
    longDescription: "A failing roof coating doesn't just look bad — it allows water infiltration that can cause tens of thousands of dollars in structural damage, mold growth, and interior destruction. Danis Painting Co. provides professional roof coating and repair services for flat roofs, low-slope roofs, and tile roofs throughout Santa Barbara, Ventura, and Los Angeles Counties. We are certified applicators of GE Enduris High Solids Silicone Roof Coating — one of the most advanced roof protection systems available. GE Enduris creates a seamless, waterproof membrane directly over your existing roof substrate, eliminating the need for costly tear-off and replacement in most cases. The silicone formula is inherently flame-retardant, UV-stable, and remains flexible across extreme temperature ranges — critical for California's hot summers and cold winter nights. Reflective white formulas can reduce roof surface temperatures by up to 80°F, significantly lowering cooling costs. Our roof repair services address ponding water areas, blistering, cracking, failed seams, and deteriorated flashings before coating application. We also repair and re-coat tile roofs, foam roofs, modified bitumen, and TPO membranes. Every project includes a detailed inspection report and a written warranty.",
    benefits: [
      "GE Enduris certified applicators — premium silicone roof coating system",
      "Seamless waterproof membrane eliminates leaks without costly tear-off",
      "Flame-retardant and UV-stable formula for California's climate",
      "Reflective coatings reduce roof temperatures by up to 80°F, lowering energy bills",
      "Works on flat, low-slope, foam, tile, TPO, and modified bitumen roofs",
      "Extends roof lifespan by 10–20 years at a fraction of replacement cost",
      "Comprehensive inspection and repair of all problem areas before coating",
      "Written warranty on materials and workmanship",
    ],
    process: [
      "Detailed roof inspection: we walk the entire roof surface, document all problem areas, and provide a written assessment",
      "Moisture survey: we identify areas of trapped moisture that must be addressed before coating",
      "Repairs: patching of cracks, blisters, failed seams, and deteriorated flashings; re-securing loose substrate",
      "Surface preparation: thorough pressure washing and cleaning to remove dirt, algae, and chalking",
      "Primer application: where required for adhesion on specific substrates",
      "GE Enduris coating application: applied at the specified mil thickness in two coats for a seamless waterproof membrane",
      "Final inspection and warranty documentation: we walk the roof with you and provide written warranty paperwork",
    ],
    image: "/images/roof-coating.jpg",
    icon: "Shield",
    metaDescription: "Roof coating and repair services in Santa Barbara, Ventura, and Los Angeles. GE Enduris certified applicators. Flame-retardant, waterproof, energy-saving. Free inspection!",
    metaKeywords: "roof coating Santa Barbara, roof repair, GE Enduris coating, flame-retardant roof coating, roof contractor, silicone roof coating Los Angeles, flat roof coating Ventura, roof coating contractors",
  },
  "waterproofing-santa-barbara": {
    slug: "waterproofing-santa-barbara",
    title: "Waterproofing Services in Santa Barbara",
    shortTitle: "Waterproofing",
    description: "Long-lasting waterproofing protection for homes and commercial properties",
    longDescription: "Water is the single greatest threat to the structural integrity of any building. In Southern California, winter rains, coastal humidity, and temperature fluctuations create the perfect conditions for water infiltration through walls, decks, balconies, foundations, and below-grade spaces. Danis Painting Co. provides comprehensive waterproofing services for residential and commercial properties throughout Santa Barbara, Ventura, and Los Angeles Counties. Our waterproofing solutions address the most common failure points: exterior stucco walls, concrete balconies and walkways, wood decks, retaining walls, planter boxes, and below-grade foundation walls. We use elastomeric waterproofing membranes, crystalline waterproofing compounds, and traffic-grade deck coatings depending on the substrate and application. For exterior walls, we apply breathable elastomeric coatings that allow moisture vapor to escape while blocking liquid water — critical for preventing paint bubbling and stucco delamination. For concrete decks and balconies, we use traffic-grade waterproofing systems that withstand foot traffic, UV exposure, and thermal movement. Every project begins with a thorough moisture assessment to identify the source of water intrusion before any product is applied — treating symptoms without addressing the source is a waste of money.",
    benefits: [
      "Prevents costly structural damage from water infiltration",
      "Stops mold and mildew growth at the source",
      "Elastomeric membranes that flex with building movement",
      "Breathable formulas for stucco walls — prevents bubbling and delamination",
      "Traffic-grade coatings for decks, balconies, and walkways",
      "Works on stucco, concrete, wood, and masonry surfaces",
      "Moisture source assessment before any product is applied",
      "Written warranty on all waterproofing installations",
    ],
    process: [
      "Moisture assessment: we identify all water intrusion points and the root cause before recommending any solution",
      "Surface preparation: grinding, patching, and cleaning of all surfaces to ensure maximum membrane adhesion",
      "Crack and joint treatment: flexible sealant applied to all cracks, control joints, and penetrations",
      "Primer application: bonding primer applied to ensure the waterproofing membrane adheres to the substrate",
      "Waterproofing membrane application: elastomeric or crystalline product applied at specified thickness",
      "Flood testing: standing water test performed on horizontal surfaces to verify waterproofing integrity",
      "Finish coat or protective topping: UV-stable finish coat or traffic-grade topping applied over the membrane",
    ],
    image: "/images/waterproofing.jpg",
    icon: "Droplets",
    metaDescription: "Waterproofing services in Santa Barbara, Ventura, and Los Angeles. Elastomeric membranes, deck coatings, mold prevention. Moisture assessment included. Free quote!",
    metaKeywords: "waterproofing Santa Barbara, water damage prevention, waterproofing contractor, deck waterproofing Los Angeles, balcony waterproofing Ventura, elastomeric waterproofing, stucco waterproofing",
  },
  "pressure-washing-santa-barbara": {
    slug: "pressure-washing-santa-barbara",
    title: "Professional Pressure Washing Services in Santa Barbara",
    shortTitle: "Pressure Washing",
    description: "Professional pressure washing to prepare surfaces and restore beauty",
    longDescription: "Proper surface preparation is the single most important factor in the longevity of any exterior paint job — and pressure washing is where that preparation begins. Danis Painting Co. provides professional pressure washing services for residential and commercial properties throughout Santa Barbara, Ventura, and Los Angeles Counties. We use commercial-grade hot and cold water pressure washing equipment capable of removing years of accumulated dirt, mold, mildew, algae, oil stains, and chalking paint from virtually any exterior surface. Before any painting project, we pressure wash the entire exterior to ensure the paint adheres to a clean, sound substrate — skipping this step is the #1 cause of premature paint failure. Our pressure washing services are also available as a standalone service for homeowners who want to refresh their property's appearance without a full repaint. We clean stucco, wood siding, concrete driveways and walkways, brick, pavers, fences, decks, patios, and commercial parking structures. We adjust pressure settings for each surface type — high pressure for concrete and masonry, lower pressure for wood and painted surfaces — to clean effectively without causing damage. Eco-friendly biodegradable cleaning solutions are used to treat mold, algae, and oil stains before rinsing.",
    benefits: [
      "Commercial-grade equipment for thorough, efficient cleaning",
      "Essential preparation step for any exterior painting project",
      "Removes mold, mildew, algae, and oil stains effectively",
      "Adjustable pressure settings protect delicate surfaces",
      "Eco-friendly biodegradable cleaning solutions",
      "Cleans stucco, wood, concrete, brick, pavers, and decks",
      "Standalone service available — no painting project required",
      "Improves curb appeal and extends the life of exterior surfaces",
    ],
    process: [
      "Surface assessment: we evaluate all surfaces to determine appropriate pressure settings and cleaning solutions",
      "Pre-treatment: biodegradable mold and algae treatment applied to affected areas and allowed to dwell",
      "Equipment setup: commercial pressure washing equipment staged with appropriate nozzles for each surface",
      "Systematic washing: working top-to-bottom and section-by-section to prevent streaking and ensure complete coverage",
      "Detail work: hand-cleaning of tight areas, corners, and decorative elements that require careful attention",
      "Rinse and inspection: thorough rinse of all surfaces followed by inspection to verify cleanliness",
      "Drying time guidance: we advise on appropriate drying time before painting or other treatments",
    ],
    image: "/images/pressure-washing.jpg",
    icon: "Waves",
    metaDescription: "Pressure washing services in Santa Barbara, Ventura, and Los Angeles. Commercial-grade equipment, eco-friendly solutions. Essential prep for exterior painting. Free quote!",
    metaKeywords: "pressure washing Santa Barbara, power washing, surface cleaning, driveway cleaning, house cleaning services, pressure washing contractor, power washing Los Angeles, pressure washing Ventura",
  },
  "drywall-repair-santa-barbara": {
    slug: "drywall-repair-santa-barbara",
    title: "Professional Drywall Repair Services in Santa Barbara",
    shortTitle: "Drywall Repair",
    description: "Professional drywall repair and patching before painting",
    longDescription: "Cracks, holes, water stains, nail pops, and damaged corners are the most common drywall problems homeowners face — and they're all visible reminders that your walls need attention before painting. Danis Painting Co. provides professional drywall repair services throughout Santa Barbara, Ventura, and Los Angeles Counties, handling everything from hairline cracks to large section replacements. Our drywall technicians are experienced with all common drywall textures found in Southern California homes: smooth finish, orange peel, knockdown, skip trowel, and popcorn ceilings. Texture matching is one of the most challenging aspects of drywall repair — an improperly matched texture is immediately obvious under raking light. Our crews have spent decades perfecting the art of blending repairs seamlessly into existing surfaces so that the repaired area is completely invisible after painting. We handle water damage repairs with particular care, first ensuring the moisture source has been eliminated before patching, then applying mold-resistant joint compound and primer to prevent future issues. Common repair scenarios we handle daily include: holes from door handles and anchors, cracks along ceiling and wall joints, water-stained areas from roof or plumbing leaks, damaged corners and edges, and full section replacements in rooms undergoing renovation.",
    benefits: [
      "Expert texture matching — orange peel, knockdown, skip trowel, smooth, and popcorn",
      "Repairs are completely invisible after painting",
      "Water damage repairs with mold-resistant materials",
      "Handles hairline cracks to full section replacements",
      "Proper moisture assessment before patching water-damaged areas",
      "All repairs primed and ready for painting — no additional prep needed",
      "Can be combined with interior painting for a complete room refresh",
      "Quality workmanship guarantee on all repairs",
    ],
    process: [
      "Damage assessment: we inspect all areas to be repaired and identify any underlying moisture or structural issues",
      "Moisture remediation: for water-damaged areas, we verify the leak source is resolved before proceeding",
      "Repair execution: patching, taping, and mudding of all damaged areas using professional-grade joint compound",
      "Sanding: multiple rounds of sanding between coats to achieve a perfectly smooth, flat surface",
      "Texture matching: careful application of matching texture using the appropriate technique for your wall type",
      "Priming: application of high-hide primer over all repaired areas to seal the compound and ensure uniform paint absorption",
      "Final inspection: we inspect all repairs under raking light to verify seamless blending before handoff",
    ],
    image: "/images/drywall-repair.jpg",
    icon: "Hammer",
    metaDescription: "Drywall repair services in Santa Barbara, Ventura, and Los Angeles. Expert texture matching, water damage repair, invisible patches. Licensed & insured. Free quote!",
    metaKeywords: "drywall repair Santa Barbara, drywall patching, wall repair, texture matching, drywall contractor, drywall repair Los Angeles, water damage repair Ventura, popcorn ceiling repair",
  },
  "deck-staircase-repair-santa-barbara": {
    slug: "deck-staircase-repair-santa-barbara",
    title: "Deck & Staircase Repair & Restoration in Santa Barbara",
    shortTitle: "Deck & Staircase Repair",
    description: "Restore and rebuild outdoor living spaces with quality craftsmanship",
    longDescription: "Outdoor decks and staircases in Southern California face a unique combination of stressors: intense UV radiation that dries and cracks wood, coastal moisture that promotes rot and mold, and heavy foot traffic that wears down surfaces and loosens fasteners. Left unaddressed, minor deck issues become major structural problems that compromise safety and require complete replacement. Danis Painting Co. provides comprehensive deck and staircase repair, restoration, and refinishing services for wood, composite, and concrete decks throughout Santa Barbara, Ventura, and Los Angeles Counties. Our deck repair services address the full spectrum of issues: rotted deck boards and joists, loose or corroded fasteners, failing ledger board connections, damaged stair stringers and treads, deteriorated railings, and cracked concrete deck surfaces. We use pressure-treated lumber, stainless steel fasteners, and composite materials where appropriate to maximize longevity in coastal environments. After structural repairs are complete, we refinish wood decks with premium penetrating oils or solid-color stains that protect the wood from UV damage and moisture while enhancing its natural beauty. For concrete decks and balconies, we apply traffic-grade waterproofing coatings that prevent water infiltration and provide a slip-resistant surface. We also handle staircase repairs for interior and exterior stairs, including tread replacement, railing repair, and baluster replacement.",
    benefits: [
      "Structural repairs that restore safety and extend deck lifespan",
      "Rotted board and joist replacement with pressure-treated lumber",
      "Stainless steel fasteners for maximum corrosion resistance in coastal environments",
      "Premium UV-resistant stains and oils for wood deck finishing",
      "Traffic-grade waterproofing for concrete decks and balconies",
      "Interior and exterior staircase repair and refinishing",
      "Prevents costly full deck replacement through timely repairs",
      "Licensed, insured, and experienced with all deck materials",
    ],
    process: [
      "Structural inspection: we assess all deck components — boards, joists, ledger, posts, footings, railings, and stairs",
      "Damage documentation: we provide a detailed written report of all issues found and recommended repairs",
      "Material sourcing: we select appropriate replacement materials matched to your existing deck or upgraded for longevity",
      "Structural repairs: replacement of rotted boards, joists, and fasteners; reinforcement of ledger and post connections",
      "Surface preparation: sanding, cleaning, and stripping of old finish from all surfaces to be refinished",
      "Finishing: application of premium stain, oil, or waterproofing coating with appropriate number of coats",
      "Final safety inspection: we test all railings, stairs, and connections to verify structural integrity before sign-off",
    ],
    image: "/images/deck-repair.jpg",
    icon: "Wrench",
    metaDescription: "Deck and staircase repair services in Santa Barbara, Ventura, and Los Angeles. Structural repairs, wood refinishing, concrete waterproofing. Licensed & insured. Free quote!",
    metaKeywords: "deck repair Santa Barbara, staircase repair, deck restoration, outdoor living space, deck builder, deck repair Los Angeles, deck refinishing Ventura, deck waterproofing",
  },
  "trim-molding-santa-barbara": {
    slug: "trim-molding-santa-barbara",
    title: "Trim & Molding Painting Services in Santa Barbara",
    shortTitle: "Trim & Molding",
    description: "Detail-oriented painting of baseboards, crown molding, and architectural elements",
    longDescription: "Trim and molding painting is where the true quality of a paint job is revealed. Crisp, clean lines between walls and trim, perfectly smooth baseboards, and elegantly painted crown molding are the hallmarks of a professionally executed interior. Danis Painting Co. specializes in detail-oriented trim and molding painting throughout Santa Barbara, Ventura, and Los Angeles Counties. We paint all interior trim elements: baseboards, door and window casings, crown molding, chair rails, wainscoting, coffered ceilings, built-in cabinetry, fireplace mantels, and decorative millwork. Trim painting requires a different skill set than wall painting — it demands steady hands, precise masking, and the right paint formulation. We use semi-gloss and gloss alkyd and water-based enamels that cure to a hard, washable surface that resists scuffs and cleans easily. Our process includes thorough sanding of all trim surfaces to remove old brush marks and imperfections, filling of nail holes and gaps with paintable caulk, and careful back-masking of walls to achieve razor-sharp paint lines. For new construction and renovation projects, we coordinate with contractors to paint trim before installation when possible — the most efficient approach for achieving a factory-smooth finish. We also handle exterior trim painting, including fascia boards, soffits, window surrounds, shutters, and decorative brackets.",
    benefits: [
      "Razor-sharp paint lines between walls and trim -- the mark of true craftsmanship",
      "Hard semi-gloss and gloss enamels that resist scuffs and clean easily",
      "Covers all trim elements: baseboards, crown molding, casings, chair rails, wainscoting",
      "Exterior trim painting: fascia, soffits, shutters, and decorative brackets",
      "Thorough sanding removes old brush marks for a factory-smooth finish",
      "Nail hole filling and caulking included for a seamless, professional result",
      "Can be combined with interior wall painting for a complete room transformation",
      "30+ years of experience with all architectural styles across Southern California",
    ],
    process: [
      "Assessment: we inspect all trim surfaces for damage, peeling, nail holes, and gaps that need attention",
      "Sanding: thorough sanding of all trim surfaces to remove old brush marks and create a smooth base",
      "Filling and caulking: nail holes filled with spackle, gaps between trim and wall filled with paintable caulk",
      "Masking: careful back-masking of adjacent wall surfaces to achieve razor-sharp paint lines",
      "Priming: application of high-adhesion primer on bare wood and repaired areas",
      "Painting: two coats of premium semi-gloss or gloss enamel applied with professional brushes for a smooth finish",
      "Final inspection: we inspect all trim under raking light and address any imperfections before sign-off",
    ],
    image: "/images/trim-molding.jpg",
    icon: "HomeIcon",
    metaDescription: "Trim and molding painting services in Santa Barbara, Ventura, and Los Angeles. Expert baseboards, crown molding, and architectural painting. Licensed & insured. Free quote!",
    metaKeywords: "trim painting Santa Barbara, crown molding painting, baseboard painting, trim and molding, architectural painting, trim painting contractor, trim painting Los Angeles, baseboard painting Ventura",
  },
  "commercial-painting-santa-barbara": {
    slug: "commercial-painting-santa-barbara",
    title: "Commercial Painting Santa Barbara - Professional Business Painting Services",
    shortTitle: "Commercial Painting",
    description: "Expert commercial painting services for offices, retail stores, restaurants, warehouses, and industrial facilities in Santa Barbara. Licensed, insured, 30+ years experience.",
    longDescription: "Danis Painting Co. is Santa Barbara's premier commercial painting contractor with over 30 years of experience transforming businesses. We specialize in professional painting services for office buildings, retail storefronts, restaurants, medical facilities, warehouses, and industrial properties. Our commercial painting team understands that your business can't afford downtime. We work efficiently around your schedule—including evenings, weekends, and holidays—to minimize disruption while delivering exceptional results. Whether you need a complete building exterior refresh, interior office repainting, or specialized coatings for industrial applications, our licensed and insured team brings commercial-grade expertise to every project. We serve property managers, business owners, and commercial real estate developers throughout Santa Barbara County, Ventura County, and Los Angeles County.",
    benefits: [
      "Zero disruption to business operations - flexible evening and weekend scheduling",
      "30+ years commercial painting experience serving 500+ businesses",
      "Commercial-grade paints, epoxy coatings, and industrial finishes",
      "Expert color consultation aligned with your brand identity",
      "Licensed and insured California contractor with comprehensive coverage",
      "OSHA-compliant safety protocols and trained commercial crews",
      "Warranty-backed workmanship - 5-year guarantee on all commercial projects",
      "Specialized services: warehouse floor coatings, anti-graffiti coatings, fire-resistant paints",
      "Free on-site estimates with detailed project proposals and timelines",
      "Property manager preferred vendor - references available upon request",
    ],
    process: [
      "Initial consultation: On-site walkthrough, scope assessment, and business needs analysis",
      "Detailed proposal: Itemized quote, timeline, paint specifications, and scheduling options",
      "Pre-project planning: Coordinate with property managers, schedule around business hours",
      "Surface preparation: Power washing, scraping, patching, priming - commercial-grade prep",
      "Professional application: Commercial sprayers, rollers, and brushes for optimal coverage",
      "Quality control: Multi-point inspections, touch-ups, and final walkthrough with client",
      "Post-project: Detailed maintenance recommendations and warranty documentation",
    ],
    image: "/images/commercial-painting.jpg",
    icon: "Building2",
    metaDescription: "#1 commercial painting contractor in Santa Barbara. Office, retail, warehouse & industrial painting. 30+ years experience. Licensed & insured. Evening/weekend scheduling. Free estimates.",
    metaKeywords: "commercial painting Santa Barbara, commercial painter Santa Barbara, commercial painting contractors, office painting Santa Barbara, retail store painting, warehouse painting, industrial painting contractor, business painting services, commercial painting company, Santa Barbara commercial painters, Santa Barbara commercial painting contractors",
  },
};

// Add location-agnostic aliases (without -santa-barbara suffix) for cleaner URLs
const createLocationAgnosticEntry = (santaBarbaraSlug: string, newSlug: string, newTitle: string): ServiceDetail => {
  const original = serviceDetails[santaBarbaraSlug];
  return {
    ...original,
    slug: newSlug,
    title: newTitle,
    metaDescription: original.metaDescription.replace(/ in Santa Barbara/g, '').replace(/ Santa Barbara/g, ''),
    metaKeywords: original.metaKeywords.replace(/ Santa Barbara/g, '').replace(/Santa Barbara, /g, '').replace(/Santa Barbara/g, ''),
  };
};

serviceDetails["cabinet-painting"] = createLocationAgnosticEntry("cabinet-painting-santa-barbara", "cabinet-painting", "Cabinet Painting");
serviceDetails["interior-exterior-painting"] = createLocationAgnosticEntry("interior-exterior-painting-santa-barbara", "interior-exterior-painting", "Interior & Exterior Painting");
serviceDetails["exterior-painting"] = createLocationAgnosticEntry("exterior-painting-santa-barbara", "exterior-painting", "Exterior Painting");
serviceDetails["roof-coating-repair"] = createLocationAgnosticEntry("roof-coating-repair-santa-barbara", "roof-coating-repair", "Roof Coating & Repair");
serviceDetails["waterproofing"] = createLocationAgnosticEntry("waterproofing-santa-barbara", "waterproofing", "Waterproofing");
serviceDetails["pressure-washing"] = createLocationAgnosticEntry("pressure-washing-santa-barbara", "pressure-washing", "Pressure Washing");
serviceDetails["drywall-repair"] = createLocationAgnosticEntry("drywall-repair-santa-barbara", "drywall-repair", "Drywall Repair");
serviceDetails["deck-staircase-repair"] = createLocationAgnosticEntry("deck-staircase-repair-santa-barbara", "deck-staircase-repair", "Deck & Staircase Repair");
serviceDetails["trim-molding"] = createLocationAgnosticEntry("trim-molding-santa-barbara", "trim-molding", "Trim & Molding Painting");
serviceDetails["commercial-painting"] = createLocationAgnosticEntry("commercial-painting-santa-barbara", "commercial-painting", "Commercial Painting");

// Specialty finish pages — standalone (not derived from santa-barbara entries)
serviceDetails["venetian-plaster"] = {
  slug: "venetian-plaster",
  title: "Venetian Plaster Services in Santa Barbara, Ventura & Los Angeles",
  shortTitle: "Venetian Plaster",
  description: "Authentic Venetian plaster finishes — burnished marble-like walls and ceilings for luxury interiors",
  longDescription: "Venetian plaster is one of the most sophisticated wall finishes available — a centuries-old Italian technique that creates a luminous, marble-like surface with extraordinary depth and texture. Unlike paint or wallpaper, genuine Venetian plaster (known as stucco veneziano or marmorino) is composed of slaked lime putty and marble dust, applied in multiple thin coats and burnished to a high sheen with a steel trowel. The result is a surface that catches and reflects light in a way no other finish can replicate.\n\nDanis Painting Co. has applied Venetian plaster throughout Santa Barbara, Montecito, Ventura, and Los Angeles Counties for over 30 years. Our craftsmen are trained in both traditional Italian application techniques and modern lime-based products from leading manufacturers including Keim, Novacolor, and San Marco. We work on feature walls, fireplace surrounds, entryways, dining rooms, master bedrooms, and entire estate interiors where the finish is the design statement.\n\nThe application process requires skill and patience. Each coat must be applied at the correct thickness, allowed to partially cure, then burnished with increasing pressure to achieve the characteristic sheen. Venetian plaster can be finished matte, satin, or high-gloss depending on the desired effect, and can be tinted to virtually any color — from classic off-white travertine tones to bold terracotta, deep charcoal, or custom colors matched to your furnishings and art collection.\n\nVenetian plaster is also a practical choice for Santa Barbara and Ventura's coastal climate. Lime-based plasters are naturally breathable, allowing moisture to pass through the wall rather than becoming trapped behind a vapor-impermeable coating. This makes them ideal for older homes with solid masonry walls, as well as new construction where indoor air quality is a priority. Lime also has natural antimicrobial properties, making Venetian plaster a hygienic choice for kitchens, bathrooms, and healthcare environments.\n\nOur Venetian plaster services include full color consultation, sample boards for client approval, complete surface preparation (including skim coating uneven walls), multi-coat application, burnishing, and optional wax or sealer topcoat for added durability and sheen. We also offer repair and restoration of existing Venetian plaster surfaces that have been damaged or are showing their age.",
  benefits: [
    "Authentic Italian technique — slaked lime and marble dust for genuine depth and luminosity",
    "Unique finish that cannot be replicated by paint, wallpaper, or faux painting shortcuts",
    "Breathable, moisture-permeable surface ideal for Santa Barbara and Ventura's coastal climate",
    "Natural antimicrobial properties — lime-based plasters resist mold and bacteria",
    "Virtually unlimited color options — custom tinted to match any interior palette",
    "Three finish levels: matte, satin, or high-gloss burnish to suit your design vision",
    "Durable 20–30 year lifespan with proper care — far outlasts standard paint",
    "Dramatically increases property value — a recognized luxury finish in high-end real estate",
  ],
  process: [
    "Design consultation: review inspiration images, select finish level (matte/satin/gloss), and approve custom color sample boards",
    "Surface preparation: skim coat and level walls as needed — Venetian plaster requires a flat, sound substrate",
    "Primer coat: application of bonding primer compatible with lime-based plaster products",
    "First coat: thin application of Venetian plaster compound, worked in overlapping strokes to build initial texture",
    "Second coat: additional material applied while first coat is still slightly tacky, building depth and variation",
    "Burnishing: steel trowel burnishing in tight circular strokes to compact the surface and develop the characteristic sheen",
    "Optional wax or sealer: application of carnauba wax or penetrating sealer for added durability and enhanced gloss",
  ],
  image: "/images/venetian-plaster.jpg",
  icon: "Sparkles",
  metaDescription: "Authentic Venetian plaster services in Santa Barbara, Montecito, Ventura & Los Angeles. Traditional Italian lime-based plaster for luxury interiors. 30+ years experience. Free consultation.",
  metaKeywords: "venetian plaster Santa Barbara, venetian plaster Montecito, venetian plaster Ventura, venetian plaster Los Angeles, lime plaster, marmorino, stucco veneziano, luxury wall finishes, decorative plaster contractor, specialty finishes Santa Barbara",
  relatedArticles: [
    {
      slug: "venetian-plaster-vs-lime-wash",
      title: "Venetian Plaster vs. Lime Wash: Which Specialty Finish Is Right for Your Santa Barbara Home?",
      excerpt: "A detailed comparison of two premium wall finishes — covering application, aesthetics, cost, durability, and which works best for different architectural styles.",
    },
    {
      slug: "specialty-wall-finishes-santa-barbara",
      title: "Specialty Wall Finishes for Santa Barbara and Montecito Luxury Homes",
      excerpt: "How Venetian plaster, lime wash, and other specialty finishes are transforming luxury homes in Santa Barbara, Montecito, and Hope Ranch.",
    },
  ],
};

serviceDetails["lime-wash"] = {
  slug: "lime-wash",
  title: "Lime Wash Paint Services in Santa Barbara, Ventura & Los Angeles",
  shortTitle: "Lime Wash",
  description: "Authentic lime wash paint for a soft, aged, European-inspired look on interior and exterior surfaces",
  longDescription: "Limewash paint is experiencing a remarkable resurgence in interior design — and for good reason. This ancient finish, used for thousands of years on Mediterranean villas, Tuscan farmhouses, and English country estates, creates a soft, mottled, layered appearance that looks simultaneously aged and sophisticated. Unlike flat paint, limewash has translucency and variation that gives walls a living quality — the color shifts subtly as light changes throughout the day.\n\nDanis Painting Co. applies authentic limewash paint throughout Santa Barbara, Ventura, Ojai, Montecito, and Los Angeles Counties. We work with premium lime-based products including Portola Paints Lime Wash, Romabio Classico Limewash, and traditional slaked lime preparations for clients who want the most authentic result. Our applicators are trained in the specific brush techniques that create limewash's characteristic texture — the finish cannot be rolled or sprayed; it must be applied with a large, natural-bristle brush in overlapping strokes and then partially wiped back while still wet to reveal the layers beneath.\n\nLimewash is particularly well-suited to Santa Barbara and Ventura's architectural vernacular. The finish looks completely natural on Spanish Colonial stucco, Craftsman bungalows, Mediterranean Revival estates, and contemporary farmhouse interiors. It is also one of the most environmentally responsible paint finishes available — pure lime is a natural, non-toxic material with zero VOCs, and its high pH makes it naturally resistant to mold, mildew, and bacteria.\n\nFor exterior applications, limewash is an excellent choice for stucco, brick, and masonry surfaces. It bonds chemically with the substrate rather than forming a surface film, which means it won't peel, blister, or crack the way conventional exterior paint can. As it weathers, limewash develops a beautiful patina that many homeowners find more attractive than the original application. Interior limewash works beautifully on drywall, plaster, brick, and concrete, and can be applied in a single coat for a subtle wash effect or multiple coats for a richer, more saturated result.\n\nOur limewash services include color consultation (limewash colors shift significantly as they dry, so sample approval is essential), full surface preparation, primer application where required, multi-coat limewash application, and optional clear sealer for high-traffic or moisture-prone areas such as bathrooms and kitchens.",
  benefits: [
    "Authentic aged, European-inspired finish with natural translucency and depth",
    "Zero-VOC, non-toxic lime formula — the most environmentally responsible paint finish available",
    "Natural mold and mildew resistance — lime's high pH inhibits biological growth",
    "Bonds chemically with stucco, brick, and masonry — will not peel or blister like film-forming paints",
    "Develops a beautiful natural patina over time on exterior surfaces",
    "Ideal for Santa Barbara's Spanish Colonial, Mediterranean, and Craftsman architecture",
    "Breathable formula allows moisture to escape — critical for older masonry and stucco walls",
    "Available in a wide range of colors — from classic white and cream to terracotta, sage, and custom tints",
  ],
  process: [
    "Consultation and sampling: select color and finish intensity; apply sample patches to review in your actual lighting conditions",
    "Surface preparation: clean, repair, and prime surfaces as required — limewash requires a porous, absorbent substrate",
    "First coat: application with large natural-bristle brush in overlapping X-pattern strokes for even coverage",
    "Wipe-back technique: while still wet, partially wipe back the first coat with a damp cloth to create the characteristic mottled variation",
    "Second coat (optional): additional coat for richer color saturation, applied with the same brush technique",
    "Dry review: limewash lightens significantly as it dries — final color approval before any additional coats",
    "Optional sealer: application of breathable penetrating sealer for bathroom, kitchen, or high-traffic applications",
  ],
  image: "/images/lime-wash.jpg",
  icon: "Layers",
  metaDescription: "Lime wash paint services in Santa Barbara, Ventura, Ojai & Los Angeles. Authentic European limewash for stucco, brick, and interior walls. Zero-VOC, natural finish. Free consultation.",
  metaKeywords: "lime wash paint Santa Barbara, limewash Santa Barbara, limewash Ventura, limewash Los Angeles, Portola Paints limewash, Romabio limewash, lime wash stucco, natural wall finishes, limewash contractor, decorative painting Santa Barbara, lime wash Ojai, lime wash Montecito",
  relatedArticles: [
    {
      slug: "venetian-plaster-vs-lime-wash",
      title: "Venetian Plaster vs. Lime Wash: Which Specialty Finish Is Right for Your Santa Barbara Home?",
      excerpt: "A detailed comparison of two premium wall finishes — covering application, aesthetics, cost, durability, and which works best for different architectural styles.",
    },
    {
      slug: "specialty-wall-finishes-santa-barbara",
      title: "Specialty Wall Finishes for Santa Barbara and Montecito Luxury Homes",
      excerpt: "How Venetian plaster, lime wash, and other specialty finishes are transforming luxury homes in Santa Barbara, Montecito, and Hope Ranch.",
    },
  ],
};

export default function ServicePage() {
  const [match, params] = useRoute("/service/:slug");
  const [service, setService] = useState<ServiceDetail | null>(null);
  const canonicalUrl = params?.slug ? `https://danispainting.net/service/${params.slug}` : 'https://danispainting.net/';

  useEffect(() => {
    if (params?.slug) {
      const serviceData = serviceDetails[params.slug];
      if (serviceData) {
        setService(serviceData);
        document.title = `${serviceData.title} | Santa Barbara, Ventura & Surrounding Areas | Danis Painting Co.`;
        
        // Update meta description
        let metaDescription = document.querySelector('meta[name="description"]');
        if (!metaDescription) {
          metaDescription = document.createElement('meta');
          metaDescription.setAttribute('name', 'description');
          document.head.appendChild(metaDescription);
        }
        metaDescription.setAttribute('content', serviceData.metaDescription);
        
        // Update meta keywords
        let metaKeywords = document.querySelector('meta[name="keywords"]');
        if (!metaKeywords) {
          metaKeywords = document.createElement('meta');
          metaKeywords.setAttribute('name', 'keywords');
          document.head.appendChild(metaKeywords);
        }
        metaKeywords.setAttribute('content', serviceData.metaKeywords);
        
        return () => {};
      }
    }
  }, [params?.slug]);

  if (!match || !service) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container py-20 text-center">
          <h2 className="text-3xl font-bold mb-4">Service Not Found</h2>
          <p className="text-gray-600 mb-6">The service page you're looking for doesn't exist.</p>
          <Link href="/">
            <Button>Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Canonical URL for SEO */}
      <CanonicalTag url={canonicalUrl} />
      
      {/* Open Graph & Twitter Card Meta Tags */}
      <OpenGraphTags
        title={`${service.title} | Danis Painting Contractor`}
        description={service.metaDescription}
        image="https://danispainting.net/logo.png"
        url={`https://danispainting.net/service/${service.slug}`}
        type="article"
      />
      
      <Header />
      
      {/* Breadcrumb Schema */}
      <BreadcrumbSchema items={[
        { label: "Home", url: "/" },
        { label: "Services", url: "/services" },
        { label: service.shortTitle, url: `/service/${service.slug}` }
      ]} />
      
      {/* Service Schema */}
      <ServiceSchema
        serviceName={service.title}
        description={service.longDescription}
        priceRange="$$-$$$"
        serviceArea={[
          "Santa Barbara", "Ventura", "Montecito", "Goleta", "Carpinteria",
          "Ojai", "Camarillo", "Solvang", "Buellton", "Santa Ynez", "Isla Vista",
          "Summerland", "Oxnard", "Los Angeles"
        ]}
        typicalDuration="P2DT8H"
        serviceType={service.shortTitle}
        provider={{
          name: "Danis Painting Co.",
          telephone: "805-403-8727",
          address: {
            streetAddress: "133 E De La Guerra St",
            addressLocality: "Santa Barbara",
            addressRegion: "CA",
            postalCode: "93101"
          }
        }}
      />
      
      {/* Service Review Schema - Reviews + AggregateRating for rich results */}
      <ServiceReviewSchema
        serviceSlug={service.slug}
        serviceName={service.shortTitle}
      />
      
      {/* HowTo Schema */}
      <HowToSchema
        name={`How to Complete ${service.shortTitle}`}
        description={`Professional step-by-step guide for ${service.shortTitle.toLowerCase()} services by Danis Painting Co.`}
        image={service.image}
        totalTime="P3D"
        supply={[
          "Premium quality paint",
          "Primer and sealant",
          "Drop cloths and protective coverings",
          "Painter's tape",
          "Sandpaper and cleaning supplies"
        ]}
        tool={[
          "Professional paint brushes",
          "Paint rollers and trays",
          "Sprayer equipment",
          "Ladder and scaffolding",
          "Surface preparation tools"
        ]}
        steps={service.process.map((step, index) => ({
          name: `Step ${index + 1}: ${step}`,
          text: step,
          url: `https://danispainting.net/service/${service.slug}#step-${index + 1}`
        }))}
      />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 md:pt-36 md:pb-20 text-white overflow-hidden" style={{ background: 'linear-gradient(135deg, #0a1628 0%, #1e3a5f 50%, #0a1628 100%)' }}>
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: 'url(https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/hero-bg_5849f6b9_e05061fa.webp)' }}></div>
        <div className="container px-4 relative z-10">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">{service.title} in Santa Barbara & Ventura</h1>
            <p className="text-xl text-white/90 mb-8">{service.longDescription}</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className="bg-[#4caf30] hover:bg-[#3d9426] text-white"
                onClick={() => {
                  trackCTAClick('call_now', 'hero');
                  window.location.href = "tel:805-403-8727";
                }}
              >
                <Phone className="mr-2 h-5 w-5" />
                Call Now: 805-403-8727
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10" onClick={() => window.dispatchEvent(new Event('openChat'))}>
                Get Free Quote
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container px-4 py-3">
          <Breadcrumb items={[
            { label: "Home", url: "/" },
            { label: "Services", url: "/services" },
            { label: service.shortTitle, url: `/service/${service.slug}` }
          ]} />
        </div>
      </div>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="container">
          <h2 className="text-3xl font-bold mb-12 text-center text-[#1e3a8a]">Why Choose Our {service.shortTitle} Services?</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {service.benefits.map((benefit, index) => (
              <div key={index} className="flex gap-4">
                <div className="flex-shrink-0">
                  <Check className="h-6 w-6 text-[#0066cc] mt-1" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">{benefit}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <h2 className="text-3xl font-bold mb-12 text-center text-[#1e3a8a]">Our {service.shortTitle} Process</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {service.process.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                  <div className="absolute -top-4 left-6 bg-[#4caf30] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                    {index + 1}
                  </div>
                  <p className="text-gray-700 mt-4">{step}</p>
                </div>
                {index < service.process.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ChevronRight className="h-6 w-6 text-gray-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Pages Section */}
      <section className="py-16 bg-gray-50">
        <div className="container px-4">
          <h2 className="text-3xl font-bold text-center mb-8">View {service.shortTitle} Services by Location</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {[
              { name: "Santa Barbara", slug: "santa-barbara" },
              { name: "Ventura", slug: "ventura" },
              { name: "Montecito", slug: "montecito" },
              { name: "Goleta", slug: "goleta" },
              { name: "Carpinteria", slug: "carpinteria" },
              { name: "Ojai", slug: "ojai" },
              { name: "Camarillo", slug: "camarillo" },
              { name: "Solvang", slug: "solvang" },
              { name: "Buellton", slug: "buellton" },
              { name: "Santa Ynez", slug: "santa-ynez" },
              { name: "Isla Vista", slug: "isla-vista" },
              { name: "Summerland", slug: "summerland" },
              { name: "Oxnard", slug: "oxnard" },
            ].map((city) => {
              // Map ServicePage slugs to ServiceLocationPage slugs
              const serviceSlugMap: Record<string, string> = {
                // Old slugs with -santa-barbara suffix
                "cabinet-painting-santa-barbara": "cabinet-painting",
                "interior-exterior-painting-santa-barbara": "interior-exterior-painting",
                "exterior-painting-santa-barbara": "interior-exterior-painting",
                "roof-coating-repair-santa-barbara": "roof-coating",
                "waterproofing-santa-barbara": "waterproofing",
                "pressure-washing-santa-barbara": "pressure-washing",
                "drywall-repair-santa-barbara": "drywall-repair",
                "deck-staircase-repair-santa-barbara": "deck-staircase-repair",
                "trim-molding-santa-barbara": "trim-molding",
                "commercial-painting-santa-barbara": "commercial-painting",
                // New location-agnostic slugs (map to ServiceLocationPage slugs)
                "cabinet-painting": "cabinet-painting",
                "interior-exterior-painting": "interior-exterior-painting",
                "exterior-painting": "interior-exterior-painting",
                "roof-coating-repair": "roof-coating",
                "waterproofing": "waterproofing",
                "pressure-washing": "pressure-washing",
                "drywall-repair": "drywall-repair",
                "deck-staircase-repair": "deck-staircase-repair",
                "trim-molding": "trim-molding",
                "commercial-painting": "commercial-painting",
              };
              const baseServiceSlug = serviceSlugMap[service.slug] || service.slug;
              
              return (
                <Link
                  key={city.slug}
                  href={`/service/${baseServiceSlug}/${city.slug}`}
                  className="p-4 bg-white border border-gray-200 rounded-lg hover:border-[#1e3a8a] hover:shadow-md transition-all text-center"
                >
                  <div className="text-sm font-medium text-[#1e3a8a]">{city.name}</div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Related Articles Section - only shown for services with relatedArticles */}
      {service.relatedArticles && service.relatedArticles.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-1 h-8 bg-[#1e3a8a] rounded-full" />
                <h2 className="text-2xl md:text-3xl font-bold text-[#1e3a8a]">
                  Learn More: Expert Guides &amp; Articles
                </h2>
              </div>
              <p className="text-gray-600 mb-8 text-lg">
                Explore our in-depth guides to help you choose the right finish for your home or project.
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                {service.relatedArticles.map((article) => (
                  <Link
                    key={article.slug}
                    href={`/blog/${article.slug}`}
                    className="group block p-6 bg-gray-50 border border-gray-200 rounded-xl hover:border-[#1e3a8a] hover:shadow-lg transition-all duration-200"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-10 h-10 bg-[#1e3a8a]/10 rounded-lg flex items-center justify-center group-hover:bg-[#1e3a8a]/20 transition-colors">
                        <ChevronRight className="w-5 h-5 text-[#1e3a8a]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-[#1e3a8a] mb-2 group-hover:underline leading-snug">
                          {article.title}
                        </h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {article.excerpt}
                        </p>
                        <span className="inline-flex items-center gap-1 mt-3 text-sm font-medium text-[#1e3a8a]">
                          Read article <ChevronRight className="w-3 h-3" />
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      <FAQSection 
        serviceSlug={service.slug.replace(/-santa-barbara$/, "").replace(/-ventura$/, "").replace(/-los-angeles$/, "")} 
        serviceName={service.shortTitle} 
      />

      {/* Google Reviews Section */}
      <section className="py-16 bg-gray-50">
        <div className="container px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-[#1e3a8a]">
            What Our Customers Say About {service.shortTitle}
          </h2>
          <GoogleReviews maxReviews={3}  />
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-16 md:py-20 bg-[#1e3a8a]">
        <div className="container px-4">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            <div className="space-y-6">
              <div>
                <p className="text-[#22d3ee] font-semibold mb-2 text-sm tracking-wider">GET IN TOUCH</p>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
                  Get Your Free {service.shortTitle} Estimate Today
                </h2>
                <p className="text-white/70 mb-6">
                  Contact us today for a free estimate on your {service.shortTitle.toLowerCase()} project. Whether it's a small touch-up or a complete renovation, we bring honest New England quality to every job.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                  <Button
                    size="lg"
                    className="bg-[#4caf30] hover:bg-[#3d9426] text-white"
                    onClick={() => {
                      trackCTAClick('call_now', 'content');
                      window.location.href = "tel:805-403-8727";
                    }}
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    805-403-8727
                  </Button>
                  <Button
                    size="lg"
                    className="bg-white text-[#1e3a8a] hover:bg-white/90"
                    onClick={() => {
                      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
                      window.dispatchEvent(new CustomEvent('openChat'));
                    }}
                  >
                    Chat With Us
                  </Button>
                </div>
              </div>

              {/* Inline form */}
              <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); trackCTAClick('get_quote', 'content'); window.dispatchEvent(new CustomEvent('openChat')); }}>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input placeholder="Your Name *" required className="w-full px-4 py-3 rounded-md bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-[#22d3ee]" />
                  <input type="email" placeholder="Email Address *" required className="w-full px-4 py-3 rounded-md bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-[#22d3ee]" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input type="tel" placeholder="Phone Number *" required className="w-full px-4 py-3 rounded-md bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-[#22d3ee]" />
                  <input placeholder="Project Type *" required className="w-full px-4 py-3 rounded-md bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-[#22d3ee]" />
                </div>
                <textarea rows={4} placeholder="Project details: square footage, rooms, timeline, special requirements…" className="w-full px-4 py-3 rounded-md bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-[#22d3ee] resize-none" />
                <Button type="submit" className="w-full bg-white hover:bg-white/90 text-[#1e3a8a] font-semibold text-base py-6">
                  Request Free Quote
                </Button>
              </form>
            </div>

            {/* Right column: contact cards */}
            <div className="space-y-4">
              <Card className="bg-white/10 border-white/20">
                <CardContent className="p-5 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#4caf30] flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">Call Mark Danis</h3>
                    <a href="tel:805-403-8727" className="text-white hover:text-[#22d3ee] transition-colors text-lg font-medium">805-403-8727</a>
                    <p className="text-white/60 text-sm mt-1">Available for calls and questions</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/10 border-white/20">
                <CardContent className="p-5 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#4caf30] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">Email Us</h3>
                    <a href="mailto:danispainting@gmail.com" className="text-white hover:text-[#22d3ee] transition-colors">danispainting@gmail.com</a>
                    <p className="text-white/60 text-sm mt-1">We'll respond within 24 hours</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/10 border-white/20">
                <CardContent className="p-5 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#4caf30] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">Service Area</h3>
                    <p className="text-white">Santa Barbara, Ventura & Los Angeles Counties</p>
                    <p className="text-white/60 text-sm mt-1">Proudly serving Southern & Central California</p>
                  </div>
                </CardContent>
              </Card>

              {/* Trust badges */}
              <div className="flex flex-wrap gap-3 pt-2">
                {["Licensed & Insured", "Free Estimates", "30+ Years Experience", "LIC #662554"].map((badge) => (
                  <div key={badge} className="flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-3 py-1.5">
                    <Check className="w-3.5 h-3.5 text-[#4caf30] flex-shrink-0" />
                    <span className="text-white/80 text-xs font-medium">{badge}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chat Widget */}
      
      {/* Footer */}
      <Footer />
    </div>
  );
}
